/**
 * Collection goal filter — filters product grid items by product tag (client-side).
 */
class MsGoalFilter extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('[data-ms-goal-toggle]');
    this.panel = this.querySelector('[data-ms-goal-panel]');
    this.labelEl = this.querySelector('[data-ms-goal-label]');
    this.countEls = this.querySelectorAll('[data-ms-goal-count]');
    this.options = this.querySelectorAll('[data-ms-goal-option]');
    this.activeFilter = 'all';
    this.defaultLabel = this.dataset.defaultLabel || 'Goal';
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.grid = document.getElementById('product-grid');
    this.items = this.grid
      ? Array.from(this.grid.querySelectorAll(':scope > li.column'))
      : [];

    this.toggle?.addEventListener('click', (event) => {
      event.preventDefault();
      this.setOpen(!this.classList.contains('is-open'));
    });

    this.options.forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault();
        this.applyFilter(
          option.dataset.msGoalTag || 'all',
          option.dataset.msGoalLabel || this.defaultLabel,
          true
        );
        this.setOpen(false);
      });
    });

    this.applyFilter('all', this.defaultLabel, false);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  setOpen(open) {
    this.classList.toggle('is-open', open);
    this.toggle?.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.panel?.toggleAttribute('hidden', !open);

    if (open) {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
    } else {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.setOpen(false);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.setOpen(false);
      this.toggle?.focus();
    }
  }

  applyFilter(tag, label, shouldScroll = false) {
    this.activeFilter = tag;
    const normalizedTag = tag.toLowerCase();
    let visibleCount = 0;

    this.options.forEach((option) => {
      const isActive =
        (option.dataset.msGoalTag || 'all') === tag ||
        (tag === 'all' && option.dataset.msGoalTag === 'all');
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.items.forEach((item) => {
      const card = item.querySelector('product-card[data-product-tags]');
      const tagsRaw = card?.dataset.productTags || '';
      const tags = tagsRaw
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);

      const show =
        tag === 'all' ||
        tags.some((productTag) => productTag === normalizedTag);

      item.hidden = !show;
      item.classList.toggle('ms-goal-filter-hidden', !show);
      if (show) {
        visibleCount += 1;
      }
    });

    if (this.labelEl) {
      this.labelEl.textContent = tag === 'all' ? this.defaultLabel : label;
    }

    this.countEls.forEach((countEl) => {
      const template = countEl.dataset.countTemplate || '__COUNT__ Products';
      countEl.textContent = template.includes('__COUNT__')
        ? template.replace('__COUNT__', String(visibleCount))
        : `${visibleCount} Products`;
    });

    if (shouldScroll && this.grid) {
      const top = this.grid.getBoundingClientRect().top + window.pageYOffset - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) - 30;
      window.scrollTo({ top, left: 0, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('ms-goal-filter')) {
  customElements.define('ms-goal-filter', MsGoalFilter);
}
